import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Switch,
  IconButton,
} from "@mui/material";
import { Close, Save } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useOrganization } from "../../context/OrganizationContext";
import { AvailabilityMode } from "../../types/organization.types";

interface OrganizationSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function OrganizationSettingsDialog({
  open,
  onClose,
}: OrganizationSettingsDialogProps) {
  const { organization, updateOrganization, loading, isOwner } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);

  // Local state for form fields
  const [orgName, setOrgName] = useState(organization?.name || "");
  const [availabilityMode, setAvailabilityMode] = useState<AvailabilityMode>(
    organization?.settings?.availabilityMode || "daily"
  );
  const [allowEditHistory, setAllowEditHistory] = useState(
    organization?.settings?.allowEditHistory ?? false
  );
  // Employee permissions
  const [allowEmployeeSetHours, setAllowEmployeeSetHours] = useState(
    organization?.settings?.allowEmployeeSetHours ?? true
  );
  const [allowEmployeeSetTimeRestrictions, setAllowEmployeeSetTimeRestrictions] = useState(
    organization?.settings?.allowEmployeeSetTimeRestrictions ?? true
  );
  const [allowEmployeeViewAllSchedules, setAllowEmployeeViewAllSchedules] = useState(
    organization?.settings?.allowEmployeeViewAllSchedules ?? false
  );

  // Sync form state when organization data loads/changes or dialog opens
  useEffect(() => {
    if (organization && open) {
      setOrgName(organization.name || "");
      setAvailabilityMode(organization.settings?.availabilityMode || "daily");
      setAllowEditHistory(organization.settings?.allowEditHistory ?? false);
      setAllowEmployeeSetHours(organization.settings?.allowEmployeeSetHours ?? true);
      setAllowEmployeeSetTimeRestrictions(organization.settings?.allowEmployeeSetTimeRestrictions ?? true);
      setAllowEmployeeViewAllSchedules(organization.settings?.allowEmployeeViewAllSchedules ?? false);
    }
  }, [organization, open]);

  // Track if form has changes
  const hasChanges =
    orgName !== (organization?.name || "") ||
    availabilityMode !== (organization?.settings?.availabilityMode || "daily") ||
    allowEditHistory !== (organization?.settings?.allowEditHistory ?? false) ||
    allowEmployeeSetHours !== (organization?.settings?.allowEmployeeSetHours ?? true) ||
    allowEmployeeSetTimeRestrictions !== (organization?.settings?.allowEmployeeSetTimeRestrictions ?? true) ||
    allowEmployeeViewAllSchedules !== (organization?.settings?.allowEmployeeViewAllSchedules ?? false);

  const handleSave = async () => {
    if (!organization) return;

    setIsSaving(true);
    try {
      await updateOrganization({
        name: orgName,
        settings: {
          ...organization.settings,
          availabilityMode,
          allowEditHistory,
          allowEmployeeSetHours,
          allowEmployeeSetTimeRestrictions,
          allowEmployeeViewAllSchedules,
        },
      });
      enqueueSnackbar("Organization settings saved", { variant: "success" });
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to save settings";
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    // Reset form to original values on close
    if (organization) {
      setOrgName(organization.name || "");
      setAvailabilityMode(organization.settings?.availabilityMode || "daily");
      setAllowEditHistory(organization.settings?.allowEditHistory ?? false);
      setAllowEmployeeSetHours(organization.settings?.allowEmployeeSetHours ?? true);
      setAllowEmployeeSetTimeRestrictions(organization.settings?.allowEmployeeSetTimeRestrictions ?? true);
      setAllowEmployeeViewAllSchedules(organization.settings?.allowEmployeeViewAllSchedules ?? false);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Organization Settings
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : !organization ? (
          <Typography variant="body1" color="text.secondary" sx={{ p: 2 }}>
            No organization found
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, py: 1 }}>
            {/* Organization Name */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                General
              </Typography>
              <TextField
                label="Organization Name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                fullWidth
                size="small"
                disabled={!isOwner}
                helperText={!isOwner ? "Only owners can change the organization name" : ""}
              />
            </Box>

            <Divider />

            {/* Scheduling Preferences */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Scheduling Preferences
              </Typography>

              <FormControl component="fieldset" disabled={!isOwner} fullWidth>
                <FormLabel component="legend" sx={{ mb: 1, fontSize: "0.875rem" }}>
                  Employee Availability Mode
                </FormLabel>
                <RadioGroup
                  value={availabilityMode}
                  onChange={(e) => setAvailabilityMode(e.target.value as AvailabilityMode)}
                >
                  <FormControlLabel
                    value="daily"
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Daily Availability
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Track availability separately for each day
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", mb: 1 }}
                  />
                  <FormControlLabel
                    value="weekly"
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Weekly Availability
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Track availability as a weekly total distributed across days
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start" }}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            <Divider />

            {/* Edit History Setting */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                History & Editing
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Allow Edit History
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Enable editing of schedules and availability for past dates
                  </Typography>
                </Box>
                <Switch
                  checked={allowEditHistory}
                  onChange={(e) => setAllowEditHistory(e.target.checked)}
                  disabled={!isOwner}
                />
              </Box>
              {!isOwner && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  Only owners can change this setting
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Employee Permissions */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Employee Permissions
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Allow employees to set hours */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Allow Employees to Set Hours
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      When enabled, employees can specify how many hours they&apos;re available each day. When disabled, they can only mark days as available or not.
                    </Typography>
                  </Box>
                  <Switch
                    checked={allowEmployeeSetHours}
                    onChange={(e) => setAllowEmployeeSetHours(e.target.checked)}
                    disabled={!isOwner}
                  />
                </Box>

                {/* Allow employees to set time restrictions */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Allow Employees to Set Time Restrictions
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      When enabled, employees can specify preferred time slots (morning, afternoon, evening, night) for their availability.
                    </Typography>
                  </Box>
                  <Switch
                    checked={allowEmployeeSetTimeRestrictions}
                    onChange={(e) => setAllowEmployeeSetTimeRestrictions(e.target.checked)}
                    disabled={!isOwner}
                  />
                </Box>

                {/* Allow employees to view all schedules */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      Allow Employees to View Team Schedule
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      When enabled, employees can view the full team schedule for the week, not just their own shifts.
                    </Typography>
                  </Box>
                  <Switch
                    checked={allowEmployeeViewAllSchedules}
                    onChange={(e) => setAllowEmployeeViewAllSchedules(e.target.checked)}
                    disabled={!isOwner}
                  />
                </Box>
              </Box>

              {!isOwner && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                  Only owners can change these settings
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={!hasChanges || isSaving || !isOwner || !organization}
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
